import React from 'react';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    content: [
      'By accessing or using USAFlightsDeals.us, you agree to be bound by these Terms and Conditions',
      'If you do not agree with any part of these terms, you must not use our services',
      'We reserve the right to modify these terms at any time with notice posted on this page',
      'Your continued use of the site after changes constitutes your acceptance',
    ],
  },
  {
    title: '2. Booking & Reservations',
    content: [
      'All flight bookings are subject to airline availability and fare rules at the time of purchase',
      'Fares displayed are not guaranteed until payment is completed and a PNR is issued',
      'It is your responsibility to ensure all passenger details (names, passport numbers) are accurate',
      'Name corrections after ticketing may incur fees charged by the airline',
      'Bookings for minors (under 18) require a supervising adult on the same reservation',
    ],
  },
  {
    title: '3. Payments',
    content: [
      'All fares are quoted in USD unless stated otherwise',
      'Payments are processed securely via PCI-DSS compliant payment gateways',
      'Your card is charged at the time of booking confirmation',
      'We reserve the right to cancel bookings if payment authorization fails',
      'Service fees are non-refundable once a booking is confirmed',
    ],
  },
  {
    title: '4. Cancellations & Changes',
    content: [
      'Cancellation and change policies are determined by the airline\'s fare rules',
      'Non-refundable fares may not be eligible for any refund upon cancellation',
      'Change fees, fare differences, and service fees may apply for modifications',
      'Requests must be submitted at least 24 hours before departure for changes',
      'No-show passengers forfeit the full ticket value unless prior notice is given',
    ],
  },
  {
    title: '5. Refunds',
    content: [
      'Eligible refunds are processed according to our Refund Policy (see separate page)',
      'Refunds are returned to the original payment method within 7–21 business days',
      'Our service fee is deducted from all refund amounts',
      'Promotional or discounted fares may be non-refundable',
    ],
  },
  {
    title: '6. Travel Documents',
    content: [
      'It is the passenger\'s sole responsibility to hold valid travel documents (passport, visa)',
      'We are not liable for denied boarding due to invalid or expired documents',
      'International routes may require a visa — check entry requirements for your destination',
      'Passport must be valid for at least 6 months beyond your travel dates for most countries',
    ],
  },
  {
    title: '7. Baggage',
    content: [
      'Baggage allowances are set by the individual airline and may vary by route and fare class',
      'Excess baggage fees are payable directly to the airline at check-in',
      'We are not responsible for lost, delayed, or damaged baggage — contact the airline directly',
    ],
  },
  {
    title: '8. Limitation of Liability',
    content: [
      'USAFlightsDeals acts as an agent for airlines and does not operate flights',
      'We are not liable for flight delays, cancellations, or disruptions caused by airlines',
      'Our maximum liability is limited to the service fee paid for the transaction in question',
      'We are not responsible for indirect, incidental, or consequential damages',
      'Force majeure events (weather, strikes, government action) are beyond our control',
    ],
  },
  {
    title: '9. Prohibited Uses',
    content: [
      'You may not use our platform for fraudulent or unlawful purposes',
      'Automated scraping, screen-scraping, or data mining is strictly prohibited',
      'Creating fake bookings or using stolen payment methods will result in account termination and legal action',
      'Reselling tickets purchased through our platform without authorization is prohibited',
    ],
  },
  {
    title: '10. Governing Law',
    content: [
      'These Terms are governed by the laws of the State of New York, USA',
      'Any disputes shall be resolved through binding arbitration in New York County',
      'If any provision is found unenforceable, the remaining provisions remain in full effect',
    ],
  },
];

export default function TermsPage() {
  return (
    <div>
      <SEO
        title="Terms & Conditions"
        canonical="/terms"
        description="USAFlightsDeals Terms and Conditions — read our terms of service covering bookings, payments, cancellations, and your rights as a traveler."
        keywords="terms and conditions, terms of service, booking terms, usaflightsdeal terms"
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-14 sm:py-20 px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
          <span>📋</span> Last Revised: June 1, 2025
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">Terms &amp; Conditions</h1>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">
          Please read these terms carefully before booking. They outline your rights and responsibilities when using USAFlightsDeals.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-16">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-10 text-sm text-blue-800">
          <strong>Note:</strong> USAFlightsDeals acts as a travel agent — not an airline. Airline fare rules govern cancellations, changes, and refunds. We are here to help navigate the process.
        </div>

        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
              <ul className="space-y-2">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 text-sm sm:text-base">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-gray-50 rounded-2xl border border-gray-200 p-6 text-center">
          <p className="text-gray-600 text-sm">Have questions about these terms?</p>
          <a
            href="mailto:support@usaflightsdeal.com"
            className="mt-2 inline-block bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-semibold px-6 py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity"
          >
            Contact Us →
          </a>
        </div>
      </div>
    </div>
  );
}
